import React, { ReactElement } from 'react'
import { useTranslate } from 'src/localization/Trns'

import { SENSITIVE_CAPTURE_ATTRIBUTES } from '../cardDetailHeroConstants'
import type {
  CardRevealDetails,
  CardRevealState,
} from '../useCardRevealDisclosure'
import * as style from './CardRevealPanel.style'

type CardRevealPanelProps = {
  /** Ties the panel to the Show details trigger via aria-controls. */
  id: string
} & (
  | {
      state: Exclude<CardRevealState, 'idle' | 'ready'>
      details?: never
    }
  | ({
      state: 'ready'
    } & CardRevealDetails)
)

/**
 * The card-details reveal disclosure — card number, expiration, and security
 * code. It has no copy controls because the OS clipboard can retain credentials
 * beyond this disclosure's local-memory lifetime. Not a modal: it is a labelled
 * region tied to the Show details trigger by id, so the page stays interactive.
 * Its open/close (Escape, outside click, timeout) is owned by the shell.
 */
const CardRevealPanel: React.FC<CardRevealPanelProps> = (
  props,
): ReactElement => {
  const translate = useTranslate()

  if (props.state !== 'ready') {
    const stateText = (() => {
      switch (props.state) {
        case 'loading':
          return translate('Loading secure card details')
        case 'temporarilyUnavailable':
          // Deliberately not the `error` copy: this is the one failure a retry
          // can actually fix, so it is the one that may honestly suggest one.
          return translate(
            'Card details are temporarily unavailable. Hide details and try again in a moment.',
          )
        case 'error':
          return translate(
            'Card details could not be loaded. Hide details and try again.',
          )
      }
    })()

    return (
      <div
        id={props.id}
        css={style.panel}
        data-testid="card-reveal-panel"
        {...SENSITIVE_CAPTURE_ATTRIBUTES}
      >
        <div
          css={style.stateMessage}
          // Only `error` carries a live role here, and deliberately so.
          //
          // `alert` is the one live role specified to announce on *insertion*,
          // which is exactly how this panel arrives — mounted with its text
          // already in place. It also earns the interruption: an error is a
          // failure of something the user just asked for.
          //
          // `loading` is announced by the hero's persistent region instead. A
          // `status` inserted alongside its own text is unreliable across
          // NVDA/JAWS/VoiceOver, and duplicating it here would only make the
          // shell's announcement land twice.
          //
          // `temporarilyUnavailable` is an error by any other name — a failure
          // of something the user just asked for, carrying an action to take —
          // so it earns the same interruption.
          role={
            props.state === 'error' || props.state === 'temporarilyUnavailable'
              ? 'alert'
              : undefined
          }
        >
          {stateText}
        </div>
      </div>
    )
  }

  const { cvc, expiry } = props

  return (
    <div
      id={props.id}
      css={style.panel}
      data-testid="card-reveal-panel"
      {...SENSITIVE_CAPTURE_ATTRIBUTES}
    >
      <div css={style.footer}>
        <span css={style.detailGroup}>
          <span css={style.detailLabel}>{translate('Expires')}</span>
          <span css={style.detailValue} data-testid="card-reveal-expiry">
            {expiry}
          </span>
        </span>
        <span css={style.detailGroup}>
          <span css={style.detailLabel}>{translate('CVC')}</span>
          <span css={style.detailValue} data-testid="card-reveal-cvc">
            {cvc}
          </span>
        </span>
      </div>
    </div>
  )
}

export default CardRevealPanel
